import { BrowserModule } from '@angular/platform-browser';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { MenuComponent } from './components/menu/menu.component';
import { FooterComponent } from './components/footer/footer.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material.module';
import { HeaderComponent } from './components/header/header.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatNativeDateModule} from '@angular/material/core';
import { CoursesComponent } from './pages/courses/courses.component';
import { ArticlesComponent } from './pages/articles/articles.component';
import { PracticesComponent } from './pages/practices/practices.component';
import { MembersComponent } from './pages/members/members.component';
import { StaffComponent } from './pages/staff/staff.component';
import { LoginComponent } from './pages/login/login.component';
import { TokenInterceptor } from './lib/token-interceptor';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorComponent } from './components/error/error.component';
import { StreamsComponent } from './pages/streams/streams.component';
import { LoadingComponent } from './components/loading/loading.component';
import { DataTableComponent } from './components/data-table/data-table.component';
import { StreamComponent } from './pages/stream/stream.component';
import { CoursePickerComponent } from './components/modals/course-picker/course-picker.component';
import { StreamEditorComponent } from './components/modals/stream-editor/stream-editor.component';
import { BsTableComponent } from './components/bs-table/bs-table.component';
import { Published } from './pipes/published.pipe';
import { CourseComponent } from './pages/course/course.component';
import { QuizComponent } from './pages/quiz/quiz.component';
import { LessonsComponent } from './pages/lessons/lessons.component';
import { TutorsComponent } from './pages/tutors/tutors.component';
import { SupportersComponent } from './pages/supporters/supporters.component';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { TutorComponent } from './pages/tutor/tutor.component';
import { MaterialFileInputModule } from 'ngx-material-file-input';
import { SafePipe } from './pipes/safe.pipe';
import { SupporterComponent } from './pages/supporter/supporter.component';
import { CategoriesComponent } from './pages/categories/categories.component';
import { CategoryComponent } from './pages/category/category.component';
import { AnimaltypesComponent } from './pages/animaltypes/animaltypes.component';
import { UserrolesComponent } from './pages/userroles/userroles.component';
import { TagsComponent } from './pages/tags/tags.component';
import { DiscountsComponent } from './pages/discounts/discounts.component';
import { FeaturedCoursesComponent } from './pages/homepage/featured-courses/featured-courses.component';
import { FeaturedLearningSeriesComponent } from './pages/homepage/featured-learning-series/featured-learning-series.component';
import { TestimonialsComponent } from './pages/homepage/testimonials/testimonials.component';
import { PracticeComponent } from './pages/practices/practice/practice.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { NotificationComponent } from './components/notification/notification.component';
import { AnimaltypeComponent } from './pages/animaltypes/animaltype/animaltype.component';
import { UserroleComponent } from './pages/userrole/userrole.component';
import { TagComponent } from './pages/tags/tag/tag.component';
import { ProgrammesComponent } from './pages/practices/practice/programmes/programmes.component';
import { PracticeContentComponent } from './pages/practices/practice/practice-content/practice-content.component';
import { ReportingComponent } from './pages/practices/practice/reporting/reporting.component';
import { OwnerComponent } from './pages/practices/practice/owner/owner.component';
import { DetailsComponent } from './pages/practices/practice/details/details.component';
import { PracticeMembersComponent } from './pages/practices/practice/practice-members/practice-members.component';
import { InviteMemberComponent } from './components/modals/invite-member/invite-member.component';
import { AddProgrammeComponent } from './components/modals/add-programme/add-programme.component';
import { ProgrammeComponent } from './pages/practices/practice/programmes/programme/programme.component';
import { AddMemberComponent } from './components/modals/add-member/add-member.component';
import { PracticeContentCourseComponent } from './pages/practices/practice/practice-content/practice-content-course/practice-content-course.component';
import { PracticeContentLessonsComponent } from './pages/practices/practice/practice-content/practice-content-course/practice-content-lessons/practice-content-lessons.component';
import { PracticeContentQuizComponent } from './pages/practices/practice/practice-content/practice-content-course/practice-content-quiz/practice-content-quiz.component';
import { PracticeContentPickerComponent } from './components/modals/practice-content-picker/practice-content-picker.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { TestimonialComponent } from './pages/homepage/testimonials/testimonial/testimonial.component';
import { DocumentsComponent } from './pages/documents/documents.component';
import { AddCoursesComponent } from './components/modals/add-courses/add-courses.component';
import { ReviewsComponent } from './pages/reviews/reviews.component';
import { ReviewComponent } from './pages/reviews/review/review.component';
import { MemberComponent } from './pages/members/member/member.component';
import { StaffMemberComponent } from './pages/staff/staff-member/staff-member.component';
import { CreateAdministratorComponent } from './components/modals/create-administrator/create-administrator.component';
import { ArticleComponent } from './pages/articles/article/article.component';
import { CPDtimeonly } from './pipes/cpdtimeonly.pipe';
import { MentorsComponent } from './pages/practices/practice/mentors/mentors.component';
import { DiscountComponent } from './pages/discounts/discount/discount.component';
import { ApplicationsComponent } from './pages/applications/applications.component';
import { ApplicationComponent } from './pages/applications/application/application.component';
import { BillingComponent } from './pages/practices/practice/billing/billing.component';
import { RecoverComponent } from './pages/recover/recover.component';
import { CompleteComponent } from './pages/recover/complete/complete.component';
import { AccountComponent } from './pages/account/account.component';
import { StreamDiscountsComponent } from './pages/streams/stream-discounts/stream-discounts.component';
import { BacsrenewalsComponent } from './pages/practices/bacsrenewals/bacsrenewals.component';
import { PracticeContentDocumentsComponent } from './pages/practices/practice/practice-content/practice-content-documents/practice-content-documents.component';
import { SlidesComponent } from './pages/homepage/slides/slides.component';
import { AddSubscriptionComponent } from './pages/subscriptions/add-subscription.component';
import { SubscriptionsComponent } from './pages/subscriptions/subscriptions.component';
import { EditSubscriptionComponent } from './pages/subscriptions/edit-subscription.component';

@NgModule({
  entryComponents: [
    CoursePickerComponent,
    StreamEditorComponent,
    BsTableComponent,
    InviteMemberComponent,
    AddProgrammeComponent,
    AddMemberComponent,
    PracticeContentPickerComponent,
    AddCoursesComponent,
    CreateAdministratorComponent
  ],
  declarations: [
    Published,
    AppComponent,
    DashboardComponent,
    MenuComponent,
    FooterComponent,
    HeaderComponent,
    CoursesComponent,
    ArticlesComponent,
    PracticesComponent,
    MembersComponent,
    StaffComponent,
    LoginComponent,
    ErrorComponent,
    StreamsComponent,
    LoadingComponent,
    DataTableComponent,
    StreamComponent,
    CoursePickerComponent,
    StreamEditorComponent,
    BsTableComponent,
    CourseComponent,
    QuizComponent,
    LessonsComponent,
    TutorsComponent,
    SupportersComponent,
    TutorComponent,
    SafePipe,
    SupporterComponent,
    CategoriesComponent,
    CategoryComponent,
    AnimaltypesComponent,
    UserrolesComponent,
    TagsComponent,
    DiscountsComponent,
    FeaturedCoursesComponent,
    FeaturedLearningSeriesComponent,
    TestimonialsComponent,
    PracticeComponent,
    FileUploadComponent,
    NotificationComponent,
    AnimaltypeComponent,
    UserroleComponent,
    TagComponent,
    ProgrammesComponent,
    PracticeContentComponent,
    ReportingComponent,
    OwnerComponent,
    DetailsComponent,
    PracticeMembersComponent,
    InviteMemberComponent,
    AddProgrammeComponent,
    ProgrammeComponent,
    AddMemberComponent,
    PracticeContentCourseComponent,
    PracticeContentLessonsComponent,
    PracticeContentQuizComponent,
    PracticeContentPickerComponent,
    TestimonialComponent,
    DocumentsComponent,
    AddCoursesComponent,
    ReviewsComponent,
    ReviewComponent,
    MemberComponent,
    StaffMemberComponent,
    CreateAdministratorComponent,
    CPDtimeonly,
    ArticleComponent,
    MentorsComponent,
    DiscountComponent,
    ApplicationsComponent,
    ApplicationComponent,
    BillingComponent,
    RecoverComponent,
    CompleteComponent,
    AccountComponent,
    StreamDiscountsComponent,
    BacsrenewalsComponent,
    PracticeContentDocumentsComponent,
    SlidesComponent,
    AddSubscriptionComponent,
    SubscriptionsComponent,
    EditSubscriptionComponent,
  ],
  imports: [
    CKEditorModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatNativeDateModule,
    HttpClientModule,
    MaterialFileInputModule,
    NgMultiSelectDropDownModule.forRoot()
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: {
      parse: { dateInput: 'DD/MM/YYYY' },
      display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY'
      }
    }}
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
